import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { MaintenanceDetailField } from '@/products/maintenance/features/maintenance-detail-field';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import { maintenanceStatusLabel } from '@/products/maintenance/features/maintenance-status';
import {
  useMaintenanceInstallation,
  useMaintenanceInstallationHistory,
} from '@/products/maintenance/features/use-maintenance';
import {
  maintenanceInstallationEditPath,
  maintenanceProjectPath,
  maintenanceProtocolArchivePath,
} from '@/products/maintenance/maintenance-paths';

const HISTORY_PAGE_SIZE = 25;

export function MaintenanceInstallationDetailPage() {
  const { installationId = '' } = useParams();
  const installation = useMaintenanceInstallation(installationId);
  const history = useMaintenanceInstallationHistory(installationId);
  const [historyPage, setHistoryPage] = useState(1);

  usePageTitle(installation.data?.name ?? 'Anlage');

  const historySlice = useMemo(() => {
    const historyItems = history.data?.items ?? [];
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return historyItems.slice(start, start + HISTORY_PAGE_SIZE);
  }, [history.data?.items, historyPage]);
  const historyTotal = history.data?.items.length ?? 0;

  if (installation.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Anlage wird geladen …
      </p>
    );
  }
  if (installation.isError || !installation.data) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Die Anlage wurde nicht gefunden.
      </p>
    );
  }

  const row = installation.data;

  return (
    <div className="space-y-6">
      <p className="text-sm">
        <Link className="text-primary hover:underline" to={maintenanceProjectPath(row.projectId)}>
          ← Zum Projekt
        </Link>
      </p>

      <PlatformPageHeader
        title={row.name}
        description={`${row.installationNumber} · ${row.project.customer.name}`}
        action={
          <Button variant="outline" className="min-h-11" asChild>
            <Link to={maintenanceInstallationEditPath(row.id)}>Bearbeiten</Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Stammdaten
            <MaintenanceStatusBadge status={row.isActive} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <MaintenanceDetailField label="Anlagenkennung" value={row.installationNumber} />
          <MaintenanceDetailField label="Anlagentyp" value={row.installationType} />
          <MaintenanceDetailField label="Standort" value={row.location} />
          <MaintenanceDetailField label="Hersteller" value={row.manufacturer ?? '—'} />
          <MaintenanceDetailField label="Modell" value={row.model ?? '—'} />
          <MaintenanceDetailField label="Seriennummer" value={row.serialNumber ?? '—'} />
          <MaintenanceDetailField label="Inbetriebnahme" value={row.commissionedOn ?? '—'} />
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Wartungshistorie</h2>
        {history.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Historie wird geladen …
          </p>
        ) : null}
        {history.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch keine abgeschlossenen Wartungsprotokolle.
          </p>
        ) : null}
        <ul className="space-y-2">
          {historySlice.map((item) => (
            <li key={item.id}>
              <Card className="gap-0 overflow-hidden py-0">
                <Link
                  to={maintenanceProtocolArchivePath(item.protocolId)}
                  className="block px-4 py-4 hover:bg-accent/40"
                >
                  <p className="font-medium text-primary">
                    {item.orderNumber} · {item.templateName} v{item.templateVersionNumber}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <PersonChip
                        displayName={item.assignedTo.displayName}
                        profilePhotoUrl={item.assignedTo.profilePhotoUrl}
                        avatarClassName="size-5"
                      />
                    </span>{' '}
                    · {maintenanceStatusLabel(item.status)} ·{' '}
                    {item.completedAt
                      ? new Date(item.completedAt).toLocaleDateString('de-DE')
                      : (item.scheduledFor ?? 'ohne Termin')}
                  </p>
                </Link>
              </Card>
            </li>
          ))}
        </ul>
        {historyTotal > 0 ? (
          <MaintenanceListFooter
            page={historyPage}
            pageSize={HISTORY_PAGE_SIZE}
            total={historyTotal}
            onPageChange={setHistoryPage}
          />
        ) : null}
      </section>
    </div>
  );
}
